import { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "context/AuthContext";
import { AxiosError } from "axios";
import type { ApiError } from "types";
import { SESSION_EXPIRED_MESSAGE_KEY } from "api/axios";

const schema = z.object({
  email: z.string().email("Valid email is required"),
  password: z.string().min(1, "Password is required"),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState("");
  const [sessionMessage, setSessionMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const message = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY);
    if (message) {
      setSessionMessage(message);
      sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError("");
    setSessionMessage("");
    try {
      await login(data.email, data.password);
      navigate("/", { replace: true });
    } catch (err) {
      const axiosErr = err as AxiosError<ApiError>;
      const errData = axiosErr.response?.data;
      if (errData?.data?.verification_required) {
        navigate(
          `/verify?email=${encodeURIComponent(errData.data.email || data.email)}`,
          { replace: true }
        );
        return;
      }
      setError(errData?.message ?? "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Card sx={{ width: 400, p: 2 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center">
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            Customer Outbound Tool
          </Typography>

          {sessionMessage && !error && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => setSessionMessage("")}
            >
              {sessionMessage}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              autoFocus
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <Typography variant="body2" textAlign="center" mt={2}>
            <Link component={RouterLink} to="/forgot-password">
              Forgot password?
            </Link>
          </Typography>

          <Typography variant="body2" textAlign="center" mt={1}>
            Don&apos;t have an account?{" "}
            <Link component={RouterLink} to="/signup">
              Sign up
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
